import './carica-giustificativi.scss';

import React, { useState, useEffect, useCallback, useRef } from "react";
import { confirm } from 'devextreme/ui/dialog';
import { DataGrid, FileUploader, LoadPanel, Toast, Button } from 'devextreme-react';
import { DataGridRef, Column } from 'devextreme-react/data-grid';
import { useScreenSizeClass } from '../../utils/media-query';
import useApiConfiguration from '../../apiConfig';
import { useAttachmentDataSource } from '../../datasources/attachmentDataSource';
import { AllegatiTemporaneiApi } from '../../data';
import { PopupPdfViewer } from '../../components';
import { PopupPdfViewerHandle } from '../../components/pdfViewer/popupPdfViewer';

export default function CaricaGiustificativiPage() {
    const apiConfiguration = useApiConfiguration();
    const gridRef = useRef<DataGridRef>(null);
    const pdfViewerRef = useRef<PopupPdfViewerHandle>(null);
    const screenSizeClass = useScreenSizeClass();
    const fileExtensions = ['.pdf', '.jpg', '.jpeg', '.gif', '.png'];

    const [loading, setLoading] = useState<boolean>(false);
    const [pageLoaded, setPageLoaded] = useState<boolean>(false);
    const [accessToken, setAccessToken] = useState<string | undefined>(null);
    const [toastConfig, setToastConfig] = useState({ isVisible: false, isOk: true, message: '' } as { isVisible: boolean, isOk: boolean, message: string });

    useEffect(() => {
        if (pageLoaded)
            return;

        const loadToken = async () => {
            setLoading(true);

            if (typeof apiConfiguration.accessToken === 'function')
                setAccessToken(await apiConfiguration.accessToken());
            else
                setAccessToken(apiConfiguration.accessToken);

            setPageLoaded(true);
            setLoading(false);
        };

        const fetchAll = async () => {
            await Promise.all([loadToken()]);
        };

        fetchAll();
    });

    function getUrl() {
        return `${apiConfiguration.basePath}/api/Attachments/uploadFileTemporaneo`;
    };

    const handleFileUploaded = useCallback((e: any) => {
        setLoading(false);
        e.component.reset();

        gridRef.current?.instance().refresh();

        setToastConfig({
            isVisible: true,
            isOk: true,
            message: 'File caricato/i con successo'
        });
    }, []);

    const handleUploadError = useCallback((e: any) => {
        setLoading(false);

        setToastConfig({
            isVisible: true,
            isOk: false,
            message: 'Errore durante l\'upload del file'
        });
    }, []);

    const showAllegato = async (idAllegato: number) => {
        setLoading(true);

        var api = new AllegatiTemporaneiApi(apiConfiguration);
        const response = await api.getAttachmentById({ idFileAllegato: idAllegato });
        if (response?.base64) {
            const fileContent = atob(response.base64);
            const byteArray = new Uint8Array(fileContent.length);
            for (let i = 0; i < fileContent.length; i++)
                byteArray[i] = fileContent.charCodeAt(i);

            const mimeType = "application/pdf";
            const fileBlob = new Blob([byteArray], { type: mimeType });

            if (fileBlob)
                pdfViewerRef.current?.show(fileBlob);
        }

        setLoading(false);
    };

    function checkDelete(idAllegato: number) {
        const result = confirm("Confermi l'eliminazione?", "Conferma");
        result.then((dialogRes) => {
            if (dialogRes)
                deleteAllegato(idAllegato);
        });
    };

    function deleteAllegato(idAllegato: number) {
        setLoading(true);
        var api = new AllegatiTemporaneiApi(apiConfiguration);
        
        api.deleteById({ idFileAllegato: idAllegato })
        .then(() => {
            gridRef.current?.instance().refresh();

            setToastConfig({
                isVisible: true,
                isOk: true,
                message: "Cancellazione completata"
            });
        }).catch((error) => {
            console.error(error);

            setToastConfig({
                isVisible: true,
                isOk: false,
                message: "Errore durante la cancellazione"
            });
        }).finally(() => setLoading(false));
    };

    const onHidingToast = useCallback(() => {
        setToastConfig({
            ...toastConfig,
            isVisible: false
        });
    }, [toastConfig, setToastConfig]);

    return (
        <React.Fragment>
            <div className='view-list-giustificativi-carica'>
                <div className={`view-wrapper view-wrapper-list list-page ${screenSizeClass}`}>
                    {loading && <LoadPanel style={{ zIndex: 9999 }} position={"center"} visible shading shadingColor='rgba(0, 0, 0, 0.4)' />}

                    <div className="archivio-upload-page">
                        <div className="upload-box">
                            <div className="upload-box-header">
                                Caricamento allegato
                            </div>

                            <div className="upload-box-body">
                                <div className="upload-description">
                                    Caricamento file in una zona temporanea, per poi essere collegato con la spesa corretta in un secondo tempo.
                                </div>

                                <FileUploader className='fileUploader' multiple uploadMode="instantly"
                                    allowedFileExtensions={fileExtensions} name="File" uploadUrl={getUrl()}
                                    uploadHeaders={{ 'Authorization': accessToken }}
                                    onUploadStarted={() => setLoading(true)}
                                    onUploaded={(e) => handleFileUploaded(e)}
                                    onUploadError={(e) => handleUploadError(e)}
                                />
                            </div>
                        </div>

                        <div className="grid-wrapper">
                            <DataGrid
                                id="gridUpload" ref={gridRef} key="idAllegato" showBorders rowAlternationEnabled
                                dataSource={useAttachmentDataSource()} className='grid theme-dependent'
                            >
                                <Column 
                                    width={80}
                                    cellRender={(cell) => (
                                        <div style={{ display: "flex" }}>
                                            <Button icon="pdffile" hint="Vedi Allegato" stylingMode='text' onClick={() => showAllegato(cell.data.idAllegato)} />
                                            <Button icon="trash" hint="Elimina" stylingMode='text' onClick={() => checkDelete(cell.data.idAllegato)} />
                                        </div>
                                    )}
                                />

                                <Column dataField='idAllegato' visible={false} />
                                <Column dataField='fileName' caption='Nome File' />
                                <Column dataField='dataCreazione' />
                            </DataGrid>
                        </div>
                    </div>
                </div>
            </div>

            <Toast
                visible={toastConfig.isVisible}
                message={toastConfig.message}
                height={45} width={200}
                position={{ at: 'right bottom', my: 'right bottom', of: window, offset: { x: -20, y: -20 } }}
                type={toastConfig.isOk ? "success" : "error"}
                onHiding={onHidingToast} closeOnClick
                displayTime={3000}
                animation={{ show: { type: "fade", duration: 400, from: 0, to: 1 }, hide: { type: "fade", duration: 40, to: 0 } }}
            />

            <PopupPdfViewer ref={pdfViewerRef} />
        </React.Fragment>
    );
};